/**
 * Cookie Utilities for Secure Auth Management
 * 
 * Provides utilities for managing authentication-related cookies
 * with proper security settings.
 */

// import { authLogger } from '@/core/utils/logger';

interface CookieOptions {
  httpOnly?: boolean;
  secure?: boolean;
  sameSite?: 'strict' | 'lax' | 'none';
  path?: string;
  domain?: string;
  maxAge?: number; // in seconds
  expires?: Date;
}

class CookieUtils {
  /**
   * Get cookie value by name
   * Note: Can only access non-httpOnly cookies
   */
  getCookie(name: string): string | null {
    if (typeof document === 'undefined') return null;
    
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    
    if (parts.length === 2) {
      return parts.pop()?.split(';').shift() || null;
    }
    
    return null;
  }
  
  /**
   * Set a cookie with security options
   * Note: Cannot set httpOnly cookies from JavaScript
   */
  setCookie(name: string, value: string, options: CookieOptions = {}): void {
    if (typeof document === 'undefined') return;

    // Detect if we're on HTTPS (production/staging) vs HTTP (local dev)
    const isSecure = typeof window !== 'undefined' && window.location.protocol === 'https:';

    const {
      // Default to secure on HTTPS
      secure = isSecure,
      // Match backend CSRF_SAMESITE setting: 'none' on HTTPS (staging/prod), 'lax' for local dev
      // SameSite=None requires Secure flag
      sameSite = isSecure ? 'none' : 'lax',
      path = '/',
      domain,
      maxAge,
      expires,
    } = options;
    
    let cookieString = `${name}=${encodeURIComponent(value)}`;
    
    if (path) cookieString += `; Path=${path}`;
    if (domain) cookieString += `; Domain=${domain}`;
    if (maxAge) cookieString += `; Max-Age=${maxAge}`;
    if (expires) cookieString += `; Expires=${expires.toUTCString()}`;
    if (secure) cookieString += '; Secure';
    cookieString += `; SameSite=${sameSite}`;
    
    document.cookie = cookieString;
  }
  
  /**
   * Delete a cookie
   * Tries multiple attribute combinations to handle cookies set with different settings
   */
  deleteCookie(name: string, path: string = '/', domain?: string): void {
    // Try to delete with current defaults
    this.setCookie(name, '', {
      path,
      domain,
      maxAge: 0,
    });

    // Also try with explicit sameSite variations since cookies must match attributes to be deleted
    // This handles cases where cookies were set with different sameSite values
    if (typeof document !== 'undefined') {
      const isSecure = window.location.protocol === 'https:';
      const securePart = isSecure ? '; Secure' : '';
      const domainPart = domain ? `; Domain=${domain}` : '';

      // Try 'none' first since that's what staging/prod use (requires Secure)
      if (isSecure) {
        document.cookie = `${name}=; Path=${path}${domainPart}; Max-Age=0; SameSite=None; Secure`;
      }
      // Try 'lax'
      document.cookie = `${name}=; Path=${path}${domainPart}; Max-Age=0; SameSite=Lax${securePart}`;
      // Try 'strict' in case the cookie was set with that
      document.cookie = `${name}=; Path=${path}${domainPart}; Max-Age=0; SameSite=Strict${securePart}`;
    }
  }
  
  /**
   * Clear auth-related cookies (excluding CSRF)
   * Note: This only clears non-httpOnly cookies
   * httpOnly cookies must be cleared by the server
   *
   * Use clearAllAuthCookies() for complete cleanup during logout.
   */
  clearAuthCookies(): void {
    const authCookies = [
      'auth_state',
      'session_id',
      'auth_just_logged_in',
      'isAuthenticated',
    ];

    authCookies.forEach(cookieName => {
      this.deleteCookie(cookieName);
      // Also try with different paths in case they were set differently
      this.deleteCookie(cookieName, '/api');
      this.deleteCookie(cookieName, '/auth');
    });
  }

  /**
   * Clear ALL auth-related cookies including CSRF
   * This should ONLY be called during explicit logout, not on boot.
   * Clears everything to ensure a clean slate for next login.
   */
  clearAllAuthCookies(): void {
    // Clear standard auth cookies
    this.clearAuthCookies();

    // Clear CSRF cookies
    this.clearCsrfCookies();

    // Clear any other auth-related cookies that might exist
    const additionalCookies = [
      'stytch_session',
      'session_jwt',
      'jwt_access',
      'jwt_refresh',
    ];

    additionalCookies.forEach(cookieName => {
      this.deleteCookie(cookieName);
      this.deleteCookie(cookieName, '/api');
      this.deleteCookie(cookieName, '/api/auth');
      this.deleteCookie(cookieName, '/auth');
    });
  }

  /**
   * Clear CSRF-related cookies (for logout only)
   * This should only be called during explicit logout, not on boot.
   */
  clearCsrfCookies(): void {
    const csrfCookies = [
      'csrf-token',      // Primary - standardized name
      'csrf_token',      // Legacy alias (underscore variant)
      'XSRF-TOKEN',      // Legacy alias (Angular convention)
    ];

    csrfCookies.forEach(cookieName => {
      this.deleteCookie(cookieName);
      this.deleteCookie(cookieName, '/api');
      this.deleteCookie(cookieName, '/auth');
    });
  }
  
  /**
   * Get CSRF token from cookie
   */
  getCSRFTokenFromCookie(): string | null {
    return this.getCookie('csrf-token');
  }
  
  /**
   * Set CSRF token cookie (non-httpOnly so JS can read it)
   */
  setCSRFTokenCookie(token: string): void {
    // Detect if we're on HTTPS (production/staging) vs HTTP (local dev)
    const isSecure = typeof window !== 'undefined' && window.location.protocol === 'https:';

    // Standardize on hyphenated name 'csrf-token' (aligns with backend)
    this.setCookie('csrf-token', token, {
      httpOnly: false, // Must be readable by JavaScript
      secure: isSecure,
      // Match backend CSRF_SAMESITE: 'none' on HTTPS (staging/prod), 'lax' for local dev
      // SameSite=None requires Secure flag
      sameSite: isSecure ? 'none' : 'lax',
      path: '/',
      maxAge: 14400, // 4 hours to match JWT TTL and backend CSRF cookie
    });
  }
  
  /**
   * Check if cookies are enabled
   */
  areCookiesEnabled(): boolean {
    if (typeof document === 'undefined') return false;
    
    try {
      const testKey = '_cookie_test_';
      this.setCookie(testKey, '1', { maxAge: 1 });
      const enabled = this.getCookie(testKey) === '1';
      this.deleteCookie(testKey);
      return enabled;
    } catch {
      return false;
    }
  }
  
}

// Export singleton instance
export const cookieUtils = new CookieUtils();

// Export types
export type { CookieOptions };
